/**
 * Premium-balance badge for the top-right nav. Shows the user's balance in TRADING DAYS
 * (weekends + market holidays already excluded by the burn engine), with the COUNT as the
 * visually dominant element so trial/premium users see at a glance how many days they have.
 *
 * States (the Subscribe button appears in exactly the two states where there is something to sell):
 *   • granted / full access   → "Unlimited"        (ACCESS_GRANT_EMAILS — no misleading countdown)
 *   • subscriber              → "Premium"
 *   • days > 0, started       → big "X" + "days left" + SUBSCRIBE   (red when low ≤3, dot pings)
 *   • days > 0, not started   → big "X" + "days · not started" + SUBSCRIBE  (blue; pre go-live)
 *   • no days, not subscriber → "Trial ended" + SUBSCRIBE (dot pings — highest intent on the site)
 *   • signed out              → nothing (the header shows sign-up CTAs instead)
 *
 * LIVE balance: re-fetches on every navigation (pathname change) and when the tab regains focus
 * (e.g. returning from Stripe checkout), so a purchase or a burn shows without a full reload.
 * Read-only: hitting /api/user/premium-status never consumes a day.
 */
'use client'

import { useEffect, useState, useCallback } from 'react'
import { useSession } from 'next-auth/react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { Clock, Crown, Infinity as InfinityIcon } from 'lucide-react'
import { LOW_BALANCE_THRESHOLD as LOW_BALANCE } from '@/lib/premium/premiumBanner'

interface PremiumStatus {
  signedIn: boolean
  tradingDaysRemaining: number
  started: boolean
  consumptionLive: boolean
  isSubscriber: boolean
  grantedAccess: boolean
  planLabel: string
}

/**
 * ⚠ LOW_BALANCE IS IMPORTED (see the import block), NOT RETYPED HERE. It used to be
 * `const LOW_BALANCE = 3` carrying the comment "mirrors LOW_BALANCE_THRESHOLD in premiumBanner.ts"
 * — and it did not mirror it: that constant has been 5 since 2026-07-14, raised deliberately because
 * the live base clusters at a median of 5 granted days, so a 3-day threshold showed urgency to
 * almost nobody before their access lapsed.
 *
 * 🚨 THE COST OF THE DRIFT was a two-day window where the site's two conversion surfaces disagreed:
 * at 4-5 days the fixed banner said "keep your access, subscribe before it lapses" while the navbar
 * badge right beside it still rendered calm. The badge never went urgent for the exact cohort the
 * banner was retuned for.
 *
 * ⭐ A COMMENT ASSERTING A FACT ABOUT ANOTHER FILE IS NOT A MIRROR. The import is. premiumBanner.ts
 * is pure logic (no DB, no React), so pulling it into a client component costs nothing.
 */

/**
 * The one Subscribe control, so both non-subscriber states render an identical button.
 *
 * DESIGN INTENT (owner 2026-07-28: "subtle visual cues ... not overpowering but enough to draw
 * attention"). Attention here comes from CONTRAST, not motion: every other control in the header is an
 * outlined pill, so a single solid amber button is already the loudest thing up there without moving.
 * On top of that, three quiet layers:
 *   · a permanent soft halo ring — reads as "raised" rather than as an effect
 *   · hover lifts it 1px and widens the glow, so it rewards the cursor finding it
 *   · a small ping dot ONLY when the balance is low or spent
 *
 * The first version pulsed the WHOLE button on low balance. That is the overpowering version: a
 * constantly fading control in the corner of every page reads as a broken element, and users learn to
 * ignore it. A 8px dot animates the same urgency without the button itself ever flickering.
 */
function SubscribeButton({ urgent = false }: { urgent?: boolean }) {
  return (
    <Link
      href="/subscribe"
      title="Subscribe for unlimited access"
      className="group relative inline-flex items-center gap-1 rounded-full bg-amber-500 px-2.5 py-1 text-[11px] font-bold text-black
                 shadow-[0_0_0_3px_rgba(245,158,11,0.12)] transition-all duration-200
                 hover:-translate-y-px hover:bg-amber-400 hover:shadow-[0_0_16px_rgba(245,158,11,0.45)]
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
    >
      <Crown className="h-3 w-3 opacity-80 transition-opacity group-hover:opacity-100" />
      Subscribe
      {urgent && (
        <span className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-2 w-2" aria-hidden>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-200 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-200" />
        </span>
      )}
    </Link>
  )
}

export default function PremiumDaysBadge({ className = '' }: { className?: string }) {
  const { status } = useSession()
  const pathname = usePathname()
  const [s, setS] = useState<PremiumStatus | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/user/premium-status', { cache: 'no-store' })
      if (!r.ok) return
      const d = (await r.json()) as PremiumStatus
      if (d?.signedIn) setS(d)
    } catch {
      /* keep last-known state */
    }
  }, [])

  // Fetch on auth + on every navigation (pathname change) → always the live balance.
  useEffect(() => {
    if (status !== 'authenticated') return
    load()
  }, [status, pathname, load])

  // Refetch when the tab regains focus / becomes visible (e.g. back from Stripe checkout).
  useEffect(() => {
    if (status !== 'authenticated') return
    const onFocus = () => load()
    const onVisible = () => {
      if (document.visibilityState === 'visible') load()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [status, load])

  if (!s) return null

  // 1) Granted / admin — full access, never a misleading day count.
  if (s.grantedAccess) {
    return (
      <span
        title="Full access"
        className={`inline-flex items-center gap-1 rounded-md border border-violet-400/40 bg-violet-500/12 px-1.5 py-[3px] text-[10px] font-bold uppercase tracking-[0.07em] text-violet-200 ${className}`}
      >
        <InfinityIcon className="h-3 w-3" /> Unlimited
      </span>
    )
  }

  // 2) Recurring subscriber.
  if (s.isSubscriber) {
    return (
      <span
        title="Premium subscriber"
        className={`inline-flex items-center gap-1 rounded-md border border-amber-400/40 bg-amber-500/12 px-1.5 py-[3px] text-[10px] font-bold uppercase tracking-[0.07em] text-amber-200 ${className}`}
      >
        <Crown className="h-3 w-3" /> Premium
      </span>
    )
  }

  const days = s.tradingDaysRemaining

  // 4) Out of days and not a subscriber. This is the highest-intent state on the whole site — the user
  // has spent the trial and is still here — so it gets the solid button rather than a muted "Upgrade"
  // chip that reads like every other status pill in the header.
  if (days <= 0) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className}`}>
        <span className="hidden items-center rounded-md border border-zinc-500/50 bg-zinc-400/10 px-1.5 py-[3px] text-[10px] font-bold uppercase tracking-[0.07em] text-zinc-300 sm:inline-flex">
          Trial ended
        </span>
        <SubscribeButton urgent />
      </span>
    )
  }

  // 3) Positive balance — the COUNT is the dominant element.
  const notStarted = !s.started || !s.consumptionLive
  const low = !notStarted && days <= LOW_BALANCE
  const unit = `day${days === 1 ? '' : 's'}`
  const suffix = notStarted ? '· not started' : 'left'
  const tone = notStarted
    ? 'bg-blue-500/15 border-blue-400/30 text-blue-200'
    : low
      ? 'bg-red-500/15 border-red-400/40 text-red-200'
      : 'bg-amber-500/15 border-amber-400/40 text-amber-100'

  // ── COUNT + AN EXPLICIT SUBSCRIBE BUTTON (owner 2026-07-28) ──────────────────────────────────────
  // The count alone WAS the link, and that is not discoverable: a paying-intent customer asked in live
  // chat where to subscribe while this badge was on screen the whole time. Nobody reads a day counter as
  // a buy button.
  //
  // Worth knowing why this went unnoticed for so long: an ACCESS-GRANTED account (the owner's) renders
  // the "Unlimited" pill above and returns before ever reaching this branch, so the people who could
  // have spotted it were the only ones who never saw it.
  //
  // The count keeps its own link (tapping your balance to top up is a reasonable instinct) and now sits
  // next to a button that says what it does. Shown only to non-subscribers — there is nothing to sell a
  // subscriber, and the branches above already returned.
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <Link
        href="/subscribe"
        title={
          notStarted
            ? `${days} premium ${unit} - starts on your next visit once consumption goes live · get more days`
            : `${days} premium ${unit} of access remaining${low ? ' - top up soon' : ''} · get more days`
        }
        className={`inline-flex items-center gap-1 rounded-full border pl-1.5 pr-2 py-0.5 leading-none transition-colors hover:brightness-125 ${tone}`}
      >
        <Clock className="h-3 w-3 flex-shrink-0 opacity-70" />
        <span className="text-sm font-extrabold tabular-nums">{days}</span>
        <span className="text-[10px] font-medium opacity-80">{unit} {suffix}</span>
      </Link>
      <SubscribeButton urgent={low} />
    </span>
  )
}
